import { EventDataFragment } from "@graphql";
import locale from "@bonliva-crm/locale";
import {
  CircleIcon,
  Color,
  DeleteIcon,
  EditIcon,
  MoreHorizIcon,
  Popover
} from "@bonliva-crm/ui";
import {
  Box,
  ClickAwayListener,
  IconButton,
  Typography,
  makeStyles
} from "@material-ui/core";
import moment from "moment";
import React, { useState } from "react";
import { StyledHtml } from "@common/StyledHtml";

export type EventEntryProps = {
  icon: React.ReactNode;
  event: EventDataFragment;
  content: (description: string) => React.ReactNode;
  onUpdated?: () => void;
};

export const EventEntry = ({ icon, event, content, onUpdated }: EventEntryProps) => {
  const classes = useStyles({ sticky: event.Sticky });
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  return (
    <div>
      <Box className={classes.root}>
        <div style={{ zIndex: 1 }}>{icon} </div>
        <div className={classes.commentContainer}>
          <Box className={classes.topContainer}>
            <Box className={classes.textContainer}>
              <Typography variant="subtitle1">{event.CreatedBy.Name}</Typography>
              {event.CreatedOn && (
                <>
                  <CircleIcon
                    htmlColor={Color.DetailsInput}
                    style={{ width: "4px", height: "4px" }}
                  />
                  <Typography variant="subtitle1">
                    {moment(event.CreatedOn).format("DD MMMM YYYY HH:mm")}
                  </Typography>
                </>
              )}
            </Box>
            <Box className={classes.iconContainer}>
              {(event.CanDelete || event.CanEdit) && (
                <IconButton
                  className={classes.iconButton}
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                  <MoreHorizIcon />
                </IconButton>
              )}
            </Box>
          </Box>
          <StyledHtml styleProps={{ display: "inline-block" }}>
            {event.DescriptionHtml}
          </StyledHtml>
          <Popover
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left"
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left"
            }}
          >
            <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
              <div>
                <Box
                  className={classes.popoverButtonContainer}
                  onClick={() => {
                    setAnchorEl(null);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <IconButton style={{ padding: "0.25rem" }}>
                    <EditIcon htmlColor={Color.Body} />
                  </IconButton>
                  <Typography variant="body1">{locale.Edit}</Typography>
                </Box>
                <Box
                  className={classes.popoverButtonContainer}
                  onClick={() => {
                    setAnchorEl(null);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <IconButton style={{ padding: "0.25rem" }}>
                    <DeleteIcon htmlColor={Color.Body} />
                  </IconButton>
                  <Typography variant="body1">{locale.Delete}</Typography>
                </Box>
              </div>
            </ClickAwayListener>
          </Popover>
        </div>
      </Box>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "row",
    gap: "0.5rem"
  },
  topContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    columnGap: "2rem"
  },
  textContainer: {
    display: "flex",
    flexDirection: "row",
    columnGap: "0.25rem",
    alignItems: "center"
  },
  iconButton: {
    padding: "0"
  },
  iconContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center"
  },
  commentContainer: (props: { sticky: boolean }) => ({
    width: "calc(100% - 3.5rem)",
    position: "relative",
    padding: "0.5rem 0.5rem 0.5rem 0.625rem",
    backgroundColor: props.sticky ? Color.LightYellow : Color.White,
    boxShadow: `1px 6px 24px 0px ${Color.BoxShadow}`,
    border: `1px solid ${theme.palette.info.light}`,
    borderRadius: "0.125rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",

    "&::after, &::before": {
      right: "100%",
      top: "1rem",
      border: "solid transparent",
      content: '""',
      height: "0",
      width: "0",
      position: "absolute",
      pointerEvents: "none"
    },
    "&::after": {
      borderColor: "transparent",
      borderRightColor: props.sticky ? Color.LightYellow : Color.White,
      borderWidth: "4px",
      marginTop: "-4px"
    },
    "&::before": {
      borderColor: "transparent",
      borderRightColor: theme.palette.info.light,
      borderWidth: "6px",
      marginTop: "-6px"
    }
  }),
  popoverButtonContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "0.5rem",
    alignItems: "center"
  }
}));
